import { useState } from "react";
import { User, Mail, Phone, Send, CheckCircle, MessageSquare } from "lucide-react";

const formatPhone = (value) => {
  const digits = value.replace(/\D/g, "").slice(0, 10);
  if (digits.length <= 3) return digits.length ? `(${digits}` : "";
  if (digits.length <= 6) return `(${digits.slice(0, 3)})${digits.slice(3)}`;
  return `(${digits.slice(0, 3)})${digits.slice(3, 6)}-${digits.slice(6)}`;
};

export const ServiceQuoteForm = ({ serviceName }) => {
  const [form, setForm] = useState({ name: "", email: "", phone: "", message: "" });
  const [submitted, setSubmitted] = useState(false);
  const [loading, setLoading] = useState(false);

  const handleChange = (e) => {
    const { name, value } = e.target;
    if (name === "phone") {
      setForm((f) => ({ ...f, phone: formatPhone(value) }));
    } else {
      setForm((f) => ({ ...f, [name]: value }));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const API = process.env.REACT_APP_BACKEND_URL;
      await fetch(`${API}/api/contact`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ ...form, service: serviceName }),
      });
      setSubmitted(true);
    } catch {
      setSubmitted(true);
    }
    setLoading(false);
  };

  if (submitted) {
    return (
      <div className="bg-[#0c2340] rounded-2xl p-10 text-center border border-white/10" data-testid="quote-form-success">
        <div className="w-16 h-16 bg-[#f97316] rounded-full flex items-center justify-center mx-auto mb-5">
          <CheckCircle className="w-8 h-8 text-white" />
        </div>
        <h3 className="text-2xl font-bold text-white mb-2" style={{ fontFamily: 'Outfit, sans-serif' }}>Thank You!</h3>
        <p className="text-gray-400">We've received your request for {serviceName}. We'll get back to you within 24 hours.</p>
      </div>
    );
  }

  return (
    <form onSubmit={handleSubmit} className="bg-[#0c2340] rounded-2xl p-8 lg:p-10 border border-white/10 shadow-2xl" data-testid="service-quote-form">
      <div className="flex items-center gap-3 mb-2">
        <div className="w-10 h-10 bg-[#f97316] rounded-lg flex items-center justify-center">
          <Send className="w-5 h-5 text-white" />
        </div>
        <h3 className="text-2xl font-extrabold text-white" style={{ fontFamily: 'Outfit, sans-serif' }}>
          Get a Free Quote
        </h3>
      </div>
      <p className="text-gray-400 mb-7 text-sm">Fill out the form and we'll get back to you within 24 hours.</p>
      
      <div className="space-y-4">
        <div className="relative">
          <User className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-500" />
          <input
            name="name" value={form.name} onChange={handleChange} required
            placeholder="Full Name"
            className="w-full pl-11 pr-4 py-3.5 rounded-lg bg-white/5 border border-white/10 text-white placeholder-gray-500 focus:border-[#f97316] focus:ring-1 focus:ring-[#f97316] outline-none transition-all text-sm"
            data-testid="quote-name"
          />
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div className="relative">
            <Mail className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-500" />
            <input
              name="email" type="email" value={form.email} onChange={handleChange} required
              placeholder="Email Address"
              className="w-full pl-11 pr-4 py-3.5 rounded-lg bg-white/5 border border-white/10 text-white placeholder-gray-500 focus:border-[#f97316] focus:ring-1 focus:ring-[#f97316] outline-none transition-all text-sm"
              data-testid="quote-email"
            />
          </div>
          <div className="relative">
            <Phone className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-500" />
            <input
              name="phone" type="tel" value={form.phone} onChange={handleChange} required
              placeholder="(000)000-0000"
              className="w-full pl-11 pr-4 py-3.5 rounded-lg bg-white/5 border border-white/10 text-white placeholder-gray-500 focus:border-[#f97316] focus:ring-1 focus:ring-[#f97316] outline-none transition-all text-sm"
              data-testid="quote-phone"
            />
          </div>
        </div>
        <div className="relative">
          <MessageSquare className="absolute left-4 top-4 w-4 h-4 text-gray-500" />
          <textarea
            name="message" value={form.message} onChange={handleChange} rows={4}
            placeholder={`Tell us about your ${serviceName.toLowerCase()} project...`}
            className="w-full pl-11 pr-4 py-3.5 rounded-lg bg-white/5 border border-white/10 text-white placeholder-gray-500 focus:border-[#f97316] focus:ring-1 focus:ring-[#f97316] outline-none transition-all text-sm resize-none"
            data-testid="quote-message"
          />
        </div>
        <button
          type="submit" disabled={loading}
          className="w-full bg-[#f97316] hover:bg-[#ea580c] text-white font-bold py-4 rounded-lg text-base shadow-lg shadow-orange-500/25 transition-all hover:shadow-orange-500/40 disabled:opacity-50 flex items-center justify-center gap-2"
          data-testid="quote-submit"
        >
          {loading ? "Sending..." : <><Send className="w-4 h-4" /> Request Free Quote</>}
        </button>
      </div>
      <div className="flex items-center justify-center gap-2 mt-5">
        <div className="h-px flex-1 bg-white/10"></div>
        <span className="text-gray-500 text-xs">or call</span>
        <div className="h-px flex-1 bg-white/10"></div>
      </div>
      <a href="tel:2368681195" className="flex items-center justify-center gap-2 mt-3 text-[#f97316] font-bold text-sm hover:text-[#ea580c] transition-colors">
        <Phone className="w-4 h-4" /> (236)868-1195
      </a>
    </form>
  );
};

export default ServiceQuoteForm;
